import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import LeadDrawer from "@/components/LeadDrawer";
import TotalProfitWidget from "@/components/TotalProfitWidget";
import ProductTour from "@/components/ProductTour";
import FirstRunCTAs from "@/components/FirstRunCTAs";
import UpgradeBanner from "@/components/UpgradeBanner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAGE_COLORS, fmtINR, relTime } from "@/lib/leadUtils";
import { fetchOnboardingState, shouldShowTour } from "@/lib/onboarding";
import { useIsMobile } from "@/hooks/useIsMobile";

const BASIS_OPTS = [
  { v: "lead_created", l: "Lead created" },
  { v: "wedding_date", l: "Weddings" },
  { v: "payment_received", l: "Payments" },
];

export default function Dashboard() {
  const { can, studio } = useAuth();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [activity, setActivity] = useState([]);
  const [awaitingClosure, setAwaitingClosure] = useState([]);
  const [accountsKpis, setAccountsKpis] = useState(null);
  const [totalProfit, setTotalProfit] = useState(null);
  const [basis, setBasis] = useState("lead_created");
  const [drawerId, setDrawerId] = useState(null);
  // Conversion-rate widget — owner+salesperson only. Endpoint is gated by
  // `conversion_read`; for non-allowed roles we skip the fetch entirely.
  const [conversionMonth, setConversionMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [conversion, setConversion] = useState(null);
  // Module 6C.3 — onboarding state powers both the product tour and the
  // first-run CTAs. Fetched once on mount; updated locally on dismiss/
  // complete so we don't round-trip after every interaction.
  const [onboarding, setOnboarding] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const navigate = useNavigate();

  const goToCampaign = (row) => {
    const params = new URLSearchParams();
    if (row.name && row.name !== "Direct") params.set("campaign", row.name);
    if (row.platform) params.set("platform", row.platform);
    navigate(`/leads?${params.toString()}`);
  };

  const refresh = async () => {
    // Per-Layer-A gating: fetches must be guarded at the source — never fire
    // a request the role will 403 on, and never let one rejected promise in
    // a Promise.all tear down the whole batch (StrictMode then doubles it).
    // /projects/awaiting-closure is gated by `projects_read`; for Sales the
    // server returns 403 by design.
    const safe = (p) => p.catch(() => ({ data: null }));
    const [d, r, a, ac] = await Promise.all([
      safe(api.get("/dashboard", { params: { date_basis: basis } })),
      safe(api.get("/recently-viewed")),
      safe(api.get("/dashboard/recent-activity", { params: { n: 30 } })),
      can("projects_read")
        ? safe(api.get("/projects/awaiting-closure"))
        : Promise.resolve({ data: { items: [] } }),
    ]);
    if (d.data) setData(d.data);
    setRecent(r.data?.items || []);
    setActivity(a.data?.items || []);
    setAwaitingClosure(ac.data?.items || []);
    // Phase 6 — /accounts/kpis is gated to pnl_read; skip for non-Accounts/Owner.
    if (can("pnl_read")) {
      try {
        const k = await api.get("/accounts/kpis");
        setAccountsKpis(k.data);
      } catch {
        setAccountsKpis(null);
      }
    } else {
      setAccountsKpis(null);
    }
    // Total Profit is Owner-only — endpoint returns 403 for non-owners.
    if (can("total_profit")) {
      try {
        const tp = await api.get("/dashboard/total-profit");
        setTotalProfit(tp.data);
      } catch {
        setTotalProfit(null);
      }
    } else {
      setTotalProfit(null);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [basis]);

  // Conversion-rate widget — independent fetch driven by its own month
  // picker. Skipped entirely for roles without `conversion_read` so we
  // don't fire a fetch the server will 403 (same pattern as projects_read
  // on /projects/awaiting-closure above).
  useEffect(() => {
    if (!can("conversion_read")) { setConversion(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/conversion", { params: { month_tag: conversionMonth } });
        if (!cancelled) setConversion(data);
      } catch {
        if (!cancelled) setConversion(null);
      }
    })();
    return () => { cancelled = true; };
  }, [conversionMonth, can]);

  // Module 6C.3 — load onboarding state once on mount, then decide whether
  // to open the tour. We don't auto-show the tour for users who already
  // dismissed or completed it (stamps are permanent).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await fetchOnboardingState();
      if (cancelled) return;
      setOnboarding(s);
      if (shouldShowTour(s)) {
        // Tiny delay so the dashboard paints first; the tour overlay then
        // animates in over a fully-rendered surface, not a loading shell.
        setTimeout(() => setTourOpen(true), 400);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) {
    return <Layout><div data-testid="dashboard-loading" className="kicker">Loading</div></Layout>;
  }

  const h = data.headline;
  const stageRows = Object.entries(data.by_stage || {});
  const totalStage = stageRows.reduce((a, [, v]) => a + v, 0) || 1;
  // Module 6C.3 — when every KPI is zero AND there are no active leads at
  // all, surface a gentle hint that the dashboard isn't broken — the user
  // likely has only sample leads in Pipeline (which we deliberately exclude
  // from these numbers). Without this hint, "0 active leads + 2 in Pipeline"
  // reads as a bug. We don't render it once any real lead lands.
  const isFirstRunEmpty = h.total === 0
    && h.new_this_week === 0
    && h.hot_leads === 0
    && h.pipeline_value === 0
    && Object.keys(data.by_stage || {}).length === 0;

  return (
    <Layout>
      <div className="fade-up" data-testid="dashboard-page">
        <div className="flex items-end justify-between gap-4 mb-6 lg:mb-10 flex-wrap">
          <div>
            <div className="kicker mb-2 lg:mb-3" style={{ color: "var(--ds-accent)" }}>OVERVIEW</div>
            <h1
              className="font-display"
              data-testid="dashboard-title"
              style={{
                fontSize: "clamp(30px, 8.5vw, 62px)",
                color: "var(--ds-ink)",
                letterSpacing: "-0.01em",
                lineHeight: 1,
                fontWeight: 400,
              }}
            >
              Dashboard
            </h1>
          </div>
          {/* Spec §5 — white pill segmented toggle (desktop). Mobile: full-width
              pill container with rounded-999 buttons on a sand background. */}
          <div
            className={isMobile ? "flex w-full" : "flex"}
            data-testid="basis-switcher"
            style={{
              background: isMobile ? "#EFE8DA" : "#FFF",
              border: isMobile ? "none" : "1px solid var(--ds-surface-line)",
              borderRadius: isMobile ? 999 : 8,
              padding: 4,
              boxShadow: isMobile ? "inset 0 1px 2px rgba(34,56,44,0.06)" : "none",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            {BASIS_OPTS.map((o) => (
              <button
                key={o.v}
                onClick={() => setBasis(o.v)}
                data-testid={`basis-${o.v}`}
                style={{
                  flex: isMobile ? "1 1 0" : "0 0 auto",
                  minWidth: 0,
                  padding: isMobile ? "9px 6px" : "9px 18px",
                  borderRadius: isMobile ? 999 : 5,
                  fontSize: isMobile ? 10.5 : 11,
                  fontWeight: isMobile ? 700 : 600,
                  letterSpacing: isMobile ? "0.06em" : "0.13em",
                  textTransform: "uppercase",
                  background: basis === o.v ? "var(--ds-ink)" : "transparent",
                  color: basis === o.v ? "#F4EFE4" : (isMobile ? "rgba(34,56,44,0.55)" : "#857E6F"),
                  boxShadow: (isMobile && basis === o.v) ? "0 2px 6px rgba(34,56,44,0.2)" : "none",
                  transition: "all .18s ease",
                  border: "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "clip",
                }}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* Module 6C.4 Layer 2.1 — persistent upgrade banner for Trial/Free.
            Self-fetches billing usage; renders null for paid/grandfathered.
            Sits above first-run CTAs so the plan reality is the first thing
            a new studio owner sees on Dashboard. */}
        <UpgradeBanner />

        {/* Module 6C.3 — first-run CTAs (owner + import_use gated). Lives
            above the KPI strip so a new owner sees the next-step prompts
            before scrolling. Unmounts entirely once both are dismissed. */}
        <FirstRunCTAs state={onboarding} onStateChange={setOnboarding} />

        {/* Module 6C.3 — friendly hint when the dashboard is empty but
            samples may be in Pipeline. Prevents the "0 leads but I can
            see 2 in Pipeline" confusion on day 1. */}
        {isFirstRunEmpty && (
          <div
            className="mb-8 px-4 py-3 text-xs text-brand-muted"
            style={{
              background: "var(--ds-surface)",
              border: "1px solid var(--ds-surface-line)",
              borderLeft: "3px solid var(--ds-muted)",
              borderRadius: 8,
            }}
            data-testid="dashboard-empty-hint"
          >
            Your numbers appear here as real leads come in — sample leads in
            your Pipeline don&apos;t count toward these KPIs.
          </div>
        )}

        {/* Recently viewed */}
        {recent.length >= 3 && (
          <div className="mb-8" data-testid="recently-viewed-strip">
            <div className="kicker mb-3">RECENTLY VIEWED</div>
            <div className="flex gap-2 flex-wrap">
              {recent.slice(0, 5).map((r) => (
                <button
                  key={r.entity_id}
                  onClick={() => setDrawerId(r.entity_id)}
                  data-testid={`rv-${r.entity_id}`}
                  style={{
                    padding: "9px 17px",
                    border: "1px solid #E4DCCD",
                    background: "#FFF",
                    borderRadius: 9999,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#4A4439",
                    transition: "border-color .15s, color .15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--ds-accent)";
                    e.currentTarget.style.color = "var(--ds-ink)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E4DCCD";
                    e.currentTarget.style.color = "#4A4439";
                  }}
                >
                  {r.name || r.entity_id}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* KPI block — 8 individual floating pills in a 4×2 grid (spec refinement R1).
            Mobile: 2-column grid from smallest viewport (spec §4.5). */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4 mb-8"
          style={{ gap: isMobile ? 11 : 16 }}
        >
          <div data-testid="kpi-row-1" style={{ display: "contents" }}>
            <Kpi label="Active leads" value={h.total} testId="kpi-total" />
            <Kpi label="New this week" value={h.new_this_week} testId="kpi-new" />
            <Kpi label="Hot · Ace+King" value={h.hot_leads} testId="kpi-hot" />
            <Kpi
              label="Follow-ups" value={h.total ? "" : "—"} testId="kpi-followups"
              bg={(data.by_stage && (data.by_stage.Followup ?? 0)) > 0 ? "#fff" : "#fff"}
              ChildSlot={<FollowupsWidget setDrawerId={setDrawerId} />}
            />
          </div>
          <div data-testid="kpi-row-2" style={{ display: "contents" }}>
            <Kpi label="Pipeline value" value={fmtINR(h.pipeline_value)} testId="kpi-pipeline" />
            <Kpi label="Won (Onboarded)" value={fmtINR(h.won_value)} testId="kpi-won" tone="pos" />
            <Kpi label="Collected" value={fmtINR(accountsKpis?.total_collected ?? 0)} testId="kpi-collected" tone="pos" />
            <Kpi label="Outstanding" value={fmtINR(accountsKpis?.total_receivable ?? 0)} testId="kpi-outstanding" tone="neg" />
          </div>
        </div>

        {/* Slice C — CEO Total Profit widget. Endpoint returns 403 for non-owners,
            in which case `totalProfit` stays null and the widget is omitted entirely
            (not greyed-out, not hidden-in-DOM — fully absent per spec §3.3). */}
        {totalProfit && <TotalProfitWidget initialData={totalProfit} />}

        {/* Studio conversion-rate widget — owner + salesperson only.
            Gated by `conversion_read`. Placed at the top of the sales-funnel
            section (above Recent Activity / Campaign Performance) because it's
            the cohort-level summary that Campaign Performance breaks down by
            source. Read-only, cohort-based: leads CREATED in month → onboarded
            of that same cohort, regardless of when they onboard. */}
        {can("conversion_read") && (
          <ConversionPanel
            month={conversionMonth}
            onMonthChange={setConversionMonth}
            data={conversion}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.92fr] gap-5 mb-8">
          {/* Recent activity — pill-row pattern (refinement R2). Mobile
              (spec §4.8): stacked card — chip + Open → on top row,
              lead name on line 2, actor · time on line 3. */}
          <div data-testid="recent-activity-panel">
            <div className="kicker mb-4">RECENT ACTIVITY</div>
            {activity.length === 0 ? (
              <p className="text-sm italic" style={{ color: "var(--ds-text-2)" }}>No activity yet.</p>
            ) : (
              <div
                className="max-h-[460px] overflow-y-auto pr-1"
                style={{ display: "flex", flexDirection: "column", gap: isMobile ? 10 : 8 }}
              >
                {activity.map((a) => (
                  isMobile ? (
                    <div
                      key={a.id}
                      data-testid={`activity-${a.id}`}
                      style={{
                        background: "#FFFDFA",
                        borderRadius: 16,
                        padding: "14px 16px",
                        boxShadow: "0 5px 16px rgba(34,56,44,0.05), 0 1px 2px rgba(34,56,44,0.04)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 9,
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 9.5,
                            fontWeight: 800,
                            letterSpacing: "0.08em",
                            color: "#5C6B5E",
                            background: "#F1EFE6",
                            padding: "4px 9px",
                            borderRadius: 6,
                            textTransform: "uppercase",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                          }}
                        >
                          {a.type.replace(/_/g, " ")}
                        </span>
                        <button
                          onClick={() => setDrawerId(a.lead_id)}
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "var(--ds-accent)",
                            textDecoration: "none",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        >
                          Open →
                        </button>
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--ds-num-font)",
                          fontSize: 17,
                          fontWeight: 500,
                          color: "var(--ds-ink)",
                          marginBottom: 2,
                          wordBreak: "break-word",
                        }}
                      >
                        {a.lead_name || a.lead_id}
                      </div>
                      <div style={{ fontSize: 12, color: "rgba(34,56,44,0.5)" }}>
                        {a.actor} · {relTime(a.created_at)}
                      </div>
                    </div>
                  ) : (
                    <div
                      key={a.id}
                      className="flex items-center gap-3 text-sm"
                      data-testid={`activity-${a.id}`}
                      style={{
                        background: "var(--ds-surface)",
                        border: "1px solid var(--ds-surface-line)",
                        borderRadius: 8,
                        boxShadow: "0 1px 2px rgba(60,46,20,.04)",
                        padding: "12px 16px",
                      }}
                    >
                      <span
                        style={{
                          background: "#F1EFE6",
                          color: "#5C6B5E",
                          fontSize: 9.5,
                          fontWeight: 600,
                          letterSpacing: "0.1em",
                          padding: "5px 9px",
                          borderRadius: 4,
                          textTransform: "uppercase",
                          flexShrink: 0,
                        }}
                      >
                        {a.type.replace(/_/g, " ")}
                      </span>
                      <span style={{ color: "var(--ds-text)", fontSize: 13 }}>{a.lead_name || a.lead_id}</span>
                      <span style={{ color: "#9A9387", fontSize: 12 }}>{a.actor} · {relTime(a.created_at)}</span>
                      <button
                        onClick={() => setDrawerId(a.lead_id)}
                        className="ml-auto"
                        style={{
                          color: "var(--ds-accent)",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        Open →
                      </button>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Campaign performance — pill-row pattern (refinement R2). Header
              row is a non-pill kicker strip; data rows are individual pills.
              Mobile (spec §4.9): stacked-card layout with source icon-tile
              header and 2×2 stat grid (not a horizontal-scrolling table). */}
          <div className={isMobile ? "" : "overflow-x-auto"} data-testid="campaign-panel">
            <div className="kicker mb-4">CAMPAIGN PERFORMANCE</div>
            {(data.by_campaign || []).length === 0 ? (
              <p className="text-sm italic" style={{ color: "var(--ds-text-2)" }}>No campaign data yet.</p>
            ) : isMobile ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {(data.by_campaign || []).slice(0, 12).map((r) => (
                  <div
                    key={r.name}
                    onClick={() => goToCampaign(r)}
                    data-testid={`campaign-row-${r.name}`}
                    style={{
                      background: "#FFFDFA",
                      borderRadius: 20,
                      padding: 16,
                      boxShadow: "0 6px 20px rgba(34,56,44,0.06), 0 1px 2px rgba(34,56,44,0.04)",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 14,
                        paddingBottom: 12,
                        borderBottom: "1px solid rgba(34,56,44,0.08)",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "var(--ds-num-font)",
                          fontSize: 17,
                          fontWeight: 500,
                          color: "var(--ds-ink)",
                        }}
                      >
                        {r.name}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                          color: "rgba(34,56,44,0.4)",
                        }}
                      >
                        SOURCE
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
                      <MobileCampaignStat label="LEADS" value={String(r.count)} />
                      <MobileCampaignStat label="HOT" value={String(r.hot)} />
                      <MobileCampaignStat label="WON" value={String(r.won)} />
                      <MobileCampaignStat
                        label="WON ₹"
                        value={fmtINR(r.won_value)}
                        color={r.won_value > 0 ? "var(--ds-pos)" : "var(--ds-ink)"}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Header strip — sits above the pill rows, no own pill */}
                <div
                  className="px-4"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(160px,1.6fr) 70px 70px 70px minmax(110px,1fr)",
                    gap: 12,
                    fontSize: 10,
                    letterSpacing: "0.13em",
                    fontWeight: 600,
                    color: "var(--ds-muted)",
                    textTransform: "uppercase",
                    paddingBottom: 6,
                  }}
                >
                  <div>Source</div>
                  <div className="text-right">Leads</div>
                  <div className="text-right">Hot</div>
                  <div className="text-right">Won</div>
                  <div className="text-right">Won ₹</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(data.by_campaign || []).slice(0, 12).map((r) => (
                    <div
                      key={r.name}
                      onClick={() => goToCampaign(r)}
                      data-testid={`campaign-row-${r.name}`}
                      className="cursor-pointer"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(160px,1.6fr) 70px 70px 70px minmax(110px,1fr)",
                        gap: 12,
                        alignItems: "center",
                        background: "var(--ds-surface)",
                        border: "1px solid var(--ds-surface-line)",
                        borderRadius: 8,
                        boxShadow: "0 1px 2px rgba(60,46,20,.04)",
                        padding: "12px 16px",
                        fontSize: 13.5,
                        transition: "border-color .15s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--ds-accent)")}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--ds-surface-line)")}
                    >
                      <div style={{ color: "var(--ds-text)" }}>{r.name}</div>
                      <div className="text-right" style={{ fontVariantNumeric: "tabular-nums" }}>{r.count}</div>
                      <div className="text-right" style={{ fontVariantNumeric: "tabular-nums" }}>{r.hot}</div>
                      <div className="text-right" style={{ fontVariantNumeric: "tabular-nums" }}>{r.won}</div>
                      <div
                        className="text-right"
                        style={{
                          fontWeight: 500,
                          fontVariantNumeric: "tabular-nums",
                          color: r.won_value > 0 ? "var(--ds-pos)" : "#9A9387",
                        }}
                      >
                        {fmtINR(r.won_value)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Phase 5 Slice B-2 realignment — Projects awaiting closure widget.
            Owner-only lifecycle/closure surface. */}
        <ClosureWidget items={awaitingClosure} onChanged={refresh} />

        {/* Leads by stage — pill-row pattern (refinement R3). Mobile: label +
            count on top row, full-width bar below (spec §4.11). */}
        <div className="mb-8" data-testid="stage-chart">
          <div className="kicker mb-4">LEADS BY STAGE</div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? 16 : 8,
              ...(isMobile ? {
                background: "#FFFDFA",
                borderRadius: 20,
                padding: "18px 16px",
                boxShadow: "0 6px 20px rgba(34,56,44,0.06), 0 1px 2px rgba(34,56,44,0.04)",
              } : {}),
            }}
          >
            {stageRows.map(([stage, count]) => {
              const pct = (count / totalStage) * 100;
              const semColor = STAGE_COLORS[stage] || "var(--ds-text-2)";
              if (isMobile) {
                return (
                  <div key={stage}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 7 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: semColor }}>{stage}</span>
                      <span
                        style={{
                          fontFamily: "var(--ds-num-font)",
                          fontSize: 17,
                          color: "var(--ds-text)",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {count}
                      </span>
                    </div>
                    <div style={{ height: 12, borderRadius: 999, background: "#EFE8DA", overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          width: `max(7px, ${pct}%)`,
                          borderRadius: 999,
                          background: semColor,
                          transition: "width .3s ease",
                        }}
                      />
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={stage}
                  className="items-center"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "150px 1fr 72px",
                    gap: 12,
                    background: "var(--ds-surface)",
                    border: "1px solid var(--ds-surface-line)",
                    borderRadius: 8,
                    boxShadow: "0 1px 2px rgba(60,46,20,.04)",
                    padding: "14px 18px",
                  }}
                >
                  <div style={{ color: semColor, fontSize: 13.5, fontWeight: 500 }}>{stage}</div>
                  <div style={{ height: 10, background: "#F1EDE2", borderRadius: 6, overflow: "hidden" }}>
                    <div
                      style={{
                        height: 10,
                        width: `max(7px, ${pct}%)`,
                        background: semColor,
                        borderRadius: 6,
                        transition: "width .3s ease",
                      }}
                    />
                  </div>
                  <div
                    className="text-right"
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "var(--ds-text)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <LeadDrawer leadId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)} onChange={refresh} />
        {/* Module 6C.3 — Product tour. Portal-mounted; `open` is parent
            controlled. Refetches state on close so the same Dashboard
            mount doesn't re-open on a state change. */}
        <ProductTour
          open={tourOpen}
          studioName={studio?.name}
          onClose={async () => {
            setTourOpen(false);
            setOnboarding(await fetchOnboardingState());
          }}
          onCompleted={async () => {
            setOnboarding(await fetchOnboardingState());
          }}
        />
      </div>
    </Layout>
  );
}

function Kpi({ label, value, hint, testId, ChildSlot, bg, tone }) {
  // Spec §4 + refinement R1: each KPI cell is now its own floating pill
  // (white surface, 1px hairline border, 8px radius, soft shadow). When a
  // `ChildSlot` is provided, that slot is a fully self-contained overlay
  // (e.g. FollowupsWidget renders its own label + value at absolute inset 0).
  // We suppress the default label/value in that case so they don't peek out
  // from under the overlay when the paddings ever diverge.
  const isMobile = useIsMobile();
  const numColor =
    tone === "pos" ? "var(--ds-pos)" :
    tone === "neg" ? "var(--ds-neg)" :
    "var(--ds-text)";
  return (
    <div
      className="relative"
      style={{
        background: bg || (isMobile ? "#FFFDFA" : "var(--ds-surface)"),
        border: isMobile ? "none" : "1px solid var(--ds-surface-line)",
        borderRadius: isMobile ? 18 : 8,
        boxShadow: isMobile
          ? "0 6px 18px rgba(34,56,44,0.055), 0 1px 2px rgba(34,56,44,0.04)"
          : "0 1px 2px rgba(60,46,20,.04)",
        padding: isMobile ? 15 : "26px 30px",
        minHeight: isMobile ? 88 : 116,
        minWidth: 0,
        overflow: "hidden",
      }}
      data-testid={testId}
    >
      {!ChildSlot && (
        <>
          <div className="kicker mb-3">{label}</div>
          <div
            className="ds-num"
            style={{
              color: numColor,
              fontSize: isMobile ? "clamp(19px, 5.4vw, 27px)" : "clamp(28px, 3.2vw, 38px)",
              lineHeight: isMobile ? 1 : undefined,
              wordBreak: isMobile ? "break-word" : "normal",
            }}
          >
            {value}
          </div>
          {hint && <div className="text-xs mt-1" style={{ color: "var(--ds-text-2)" }}>{hint}</div>}
        </>
      )}
      {ChildSlot}
    </div>
  );
}

function FollowupsWidget({ setDrawerId }) {
  const isMobile = useIsMobile();
  const [c, setC] = useState({ overdue: 0, due_today: 0, upcoming: 0, active: 0 });
  useEffect(() => {
    api.get("/followups/counts").then(({ data }) => setC(data)).catch(() => {});
  }, []);
  const isHot = c.overdue > 0;
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        padding: isMobile ? 15 : "26px 30px",
        background: isHot
          ? (isMobile ? "linear-gradient(150deg,#F8E9E3,#F3DDD5)" : "var(--ds-neg-bg)")
          : "transparent",
        border: (isHot && isMobile) ? "1px solid rgba(162,75,59,0.18)" : "none",
        borderRadius: isMobile ? 18 : 8,
      }}
      data-testid="followups-widget"
    >
      <div className="kicker mb-3" style={{ color: isHot ? "var(--ds-neg)" : undefined }}>FOLLOW-UPS</div>
      <div
        className="ds-num"
        style={{
          color: isHot ? "var(--ds-neg)" : "var(--ds-text)",
          fontSize: isMobile ? 38 : "clamp(28px, 3.2vw, 38px)",
          lineHeight: isMobile ? 0.9 : undefined,
          marginBottom: isMobile ? 6 : undefined,
        }}
      >
        {c.active}
      </div>
      <div className="text-xs mt-1" style={{ color: isHot ? "var(--ds-neg)" : "var(--ds-text-2)", lineHeight: 1.4 }}>
        {c.overdue > 0 && <span>{c.overdue} overdue · </span>}
        {c.due_today} today · {c.upcoming} upcoming
      </div>
    </div>
  );
}

// Phase 5 Slice B-2 realignment — Projects awaiting closure.
// Owner-only widget. Shows projects in Delivered state with their 4-phase
// sign-off chips + closure action. Revert links on each ✓ chip flip a phase
// back (note required).
const CW_PHASES = ["sales", "production", "delivery", "accounts"];
const CW_LABELS = { sales: "Sales", production: "Production", delivery: "Delivery", accounts: "Accounts" };

function ClosureWidget({ items, onChanged }) {
  return (
    <div className="mb-8" data-testid="closure-widget">
      <div className="kicker mb-1">PROJECTS AWAITING CLOSURE</div>
      <p className="text-xs mb-4" style={{ color: "var(--ds-text-2)" }} data-testid="closure-widget-subtitle">
        Sign-off progress across phases. Projects can be closed once all phases are signed off.
      </p>
      {items.length === 0 ? (
        <p className="text-sm italic" style={{ color: "var(--ds-text-2)" }} data-testid="closure-widget-empty">
          No projects awaiting closure right now.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((p) => <ClosureRow key={p.id} project={p} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}

function ClosureRow({ project, onChanged }) {
  const so = project.completion_signoffs || {};
  const allDone = CW_PHASES.every((p) => (so[p] || {}).completed);
  const pendingLabels = CW_PHASES.filter((p) => !(so[p] || {}).completed).map((p) => CW_LABELS[p]);
  const [revertModal, setRevertModal] = useState(null);
  const [closeModal, setCloseModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const partner = project.partner_name ? ` & ${project.partner_name}` : "";
  const displayName = `${project.client_name}${partner}`;
  const wedDate = project.wedding_date
    ? new Date(project.wedding_date).toLocaleDateString("en-IN",
        { day: "2-digit", month: "short", year: "numeric" })
    : "";

  const closeProject = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/projects/${project.id}`, { status: "Completed" });
      toast.success("Project closed");
      setCloseModal(false);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Close failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`closure-row-${project.id}`}
      style={{
        background: "var(--ds-surface)",
        border: "1px solid var(--ds-surface-line)",
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(60,46,20,.04)",
        padding: "16px 18px",
      }}
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate" style={{ color: "var(--brand-ink)" }}
               data-testid={`closure-row-name-${project.id}`}>
            {displayName}
          </div>
          {wedDate && <div className="text-xs text-brand-muted">Wedding · {wedDate}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid={`closure-chips-${project.id}`}>
          {CW_PHASES.map((phase) => {
            const entry = so[phase] || { completed: false };
            const ok = !!entry.completed;
            const tooltip = ok
              ? `Completed${entry.completed_by ? ` by ${entry.completed_by}` : ""}${entry.completed_at ? ` on ${new Date(entry.completed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}`
              : "Pending";
            return (
              <div key={phase} className="flex items-center gap-1" title={tooltip}
                   data-testid={`closure-chip-${project.id}-${phase}`}>
                <span style={{ color: ok ? "#059669" : "var(--brand-champagne)" }}>{ok ? "✓" : "⏳"}</span>
                <span className="text-xs" style={{ color: "var(--brand-ink)" }}>{CW_LABELS[phase]}</span>
                {phase === "accounts" && (
                  <span className="px-1 py-0.5 text-[8px] tracking-[0.18em] uppercase ml-0.5"
                        style={{ background: "var(--brand-paper)", color: "var(--brand-muted)" }}
                        data-testid={`closure-auto-pill-${project.id}`}>
                    AUTO
                  </span>
                )}
                {ok && phase !== "sales" && phase !== "accounts" && (
                  <button onClick={() => setRevertModal(phase)}
                          className="text-[10px] underline text-brand-muted ml-0.5"
                          data-testid={`closure-undo-${project.id}-${phase}`}>
                    Undo
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <span title={!allDone ? `Awaiting: ${pendingLabels.join(", ")}` : ""}
              data-testid={`closure-close-wrap-${project.id}`}>
          <button
            onClick={() => setCloseModal(true)}
            disabled={!allDone}
            className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "var(--brand-champagne)" }}
            data-testid={`closure-close-${project.id}`}>
            Close project
          </button>
        </span>
      </div>

      {revertModal && (
        <ClosureRevertModal
          projectId={project.id} phase={revertModal}
          onClose={() => setRevertModal(null)}
          onReverted={() => { setRevertModal(null); onChanged && onChanged(); }}
        />
      )}
      {closeModal && (
        <ClosureConfirmModal
          projectName={displayName}
          onClose={() => setCloseModal(false)}
          onConfirm={closeProject}
          submitting={submitting}
        />
      )}
    </div>
  );
}

function ClosureRevertModal({ projectId, phase, onClose, onReverted }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    if (note.trim().length < 10) { setError("Please add a note (at least 10 characters)"); return; }
    setSaving(true); setError("");
    try {
      await api.post(`/projects/${projectId}/signoffs/${phase}/revert`, { note: note.trim() });
      toast.success(`${CW_LABELS[phase]} reopened`);
      onReverted();
    } catch (err) {
      setError(err?.response?.data?.detail || "Revert failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.5)" }} data-testid="closure-revert-modal">
      <div className="bg-white p-6 w-[min(440px,92vw)]" style={{ borderRadius: 0 }} onClick={(e) => e.stopPropagation()}>
        <div className="kicker mb-3">MARK {CW_LABELS[phase].toUpperCase()} AS NOT YET COMPLETE?</div>
        <p className="text-xs text-brand-muted mb-3">Project status will roll back accordingly. Add a reason for the activity log:</p>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                  placeholder="e.g. Editor needs to redo Wedding ceremony reel"
                  className="w-full px-3 py-2 text-sm border border-brand-border" style={{ borderRadius: 0 }}
                  data-testid="closure-revert-note" />
        <div className="text-[10px] text-brand-muted text-right mt-1">{note.trim().length} / min 10 chars</div>
        {error && <p className="text-xs text-rose-700 mt-2" data-testid="closure-revert-error">{error}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted" data-testid="closure-revert-cancel">Cancel</button>
          <button onClick={submit} disabled={saving || note.trim().length < 10}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-50"
                  style={{ background: "var(--brand-danger, #b91c1c)" }}
                  data-testid="closure-revert-confirm">
            {saving ? "Reverting…" : "Confirm revert"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClosureConfirmModal({ projectName, onClose, onConfirm, submitting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.5)" }} data-testid="closure-confirm-modal">
      <div className="bg-white p-6 w-[min(440px,92vw)]" style={{ borderRadius: 0 }} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl mb-3" style={{ color: "var(--brand-ink)" }}>Close {projectName}?</h3>
        <p className="text-sm text-brand-muted mb-5">
          All four phases are signed off. This marks the project as <em>Completed</em> and archives it from active views.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting} className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted" data-testid="closure-confirm-cancel">Cancel</button>
          <button onClick={onConfirm} disabled={submitting}
                  className="px-3 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-50"
                  style={{ background: "var(--brand-champagne)" }}
                  data-testid="closure-confirm-confirm">
            {submitting ? "Closing…" : "Close project"}
          </button>
        </div>
      </div>
    </div>
  );
}



// ──────────────────────────────────────────────────────────────────────
// ConversionPanel — sales-funnel cohort metric on the Dashboard.
// Three figures (Leads Received, Onboarded, Conversion Rate) for the
// selected month, with a month-over-month delta on the rate.
// Visible to owner + salesperson only (gated upstream by can("conversion_read")).
// ──────────────────────────────────────────────────────────────────────
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = (tag) => {
  if (!tag) return "";
  const [y, m] = tag.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

function ConversionPanel({ month, onMonthChange, data }) {
  const received = data?.leads_received ?? null;
  const onboarded = data?.onboarded ?? null;
  const rate = data?.conversion_rate;
  const prev = data?.previous_month;

  // Month-over-month delta on the rate. Only meaningful when BOTH months
  // have a rate (i.e. both had non-zero received). Anything else → omit.
  let deltaNode = null;
  if (rate != null && prev?.conversion_rate != null) {
    const delta = Math.round((rate - prev.conversion_rate) * 10) / 10;
    const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "·";
    const abs = Math.abs(delta).toFixed(1);
    deltaNode = (
      <span className="text-sm text-brand-muted ml-3" data-testid="conversion-delta">
        {arrow} {abs} pts vs {monthLabel(prev.month_tag)}
      </span>
    );
  }

  const isEmpty = received === 0;

  return (
    <div
      className="p-6 mb-8"
      style={{
        background: "var(--ds-surface)",
        border: "1px solid var(--ds-surface-line)",
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(60,46,20,.04)",
        marginTop: 22,
      }}
      data-testid="conversion-panel"
    >
      <div className="flex items-baseline justify-between mb-5 flex-wrap gap-y-2">
        <div className="kicker">CONVERSION · {monthLabel(month).toUpperCase()}</div>
        <input
          type="month"
          value={month}
          max={new Date().toISOString().slice(0, 7)}
          onChange={(e) => e.target.value && onMonthChange(e.target.value)}
          style={{
            padding: "9px 12px",
            border: "1px solid #E4DCCD",
            borderRadius: 5,
            background: "#FCFAF5",
            color: "var(--ds-text)",
            fontSize: 13,
            fontWeight: 500,
          }}
          data-testid="conversion-month-picker"
        />
      </div>

      {isEmpty ? (
        <p className="text-sm italic py-4" style={{ color: "var(--ds-text-2)" }} data-testid="conversion-empty">
          No leads received this month yet.
        </p>
      ) : data == null ? (
        <p className="text-sm italic py-4" style={{ color: "var(--ds-text-2)" }} data-testid="conversion-loading">
          Loading…
        </p>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-3"
          style={{ gap: 1, background: "var(--ds-divider)" }}
        >
          <ConversionStat
            label="Leads Received"
            value={String(received)}
            testId="conversion-received"
          />
          <ConversionStat
            label="Onboarded"
            value={String(onboarded)}
            testId="conversion-onboarded"
          />
          <div className="px-6 py-5" style={{ background: "var(--ds-surface)" }} data-testid="conversion-rate-tile">
            <div className="kicker mb-3">Conversion Rate</div>
            <div className="flex items-baseline flex-wrap gap-3">
              <div
                className="ds-num"
                style={{
                  color: "var(--ds-accent)",
                  fontSize: "clamp(30px, 3.4vw, 42px)",
                }}
                data-testid="conversion-rate-value"
              >
                {rate != null ? `${rate.toFixed(1)}%` : "—"}
              </div>
              {deltaNode}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConversionStat({ label, value, testId }) {
  return (
    <div className="px-6 py-5" style={{ background: "var(--ds-surface)" }} data-testid={testId}>
      <div className="kicker mb-3">{label}</div>
      <div
        className="ds-num"
        style={{ color: "var(--ds-text)", fontSize: "clamp(28px, 3.2vw, 38px)" }}
      >
        {value}
      </div>
    </div>
  );
}

// Phase 7.1 mobile — stacked stat cell inside a Campaign Performance card.
function MobileCampaignStat({ label, value, color }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: "rgba(34,56,44,0.45)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--ds-num-font)",
          fontSize: 21,
          color: color || "var(--ds-ink)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}
